/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { IconAlertWarningSolid, IconCalendar } from '@hulu-react-style-components/icons';
import envConfig from '@hulu/env-config';
import React from 'react';
import type { Cell, CellProps } from 'react-table';

import Badge from '../../../../common/Badge';
import DateHeaderLabel from '../../../../common/DateHeaderLabel/DateHeaderLabel';
import { computeRenderValue } from '../../../../common/EditableTableCells/EditableFrequencyCap/EditableFrequencyCap';
import { getTimezoneWithGMT } from '../../../../common/SelectTimeZone/utils';
import { resizingData, stickyBlock } from '../../../../common/TraffickingTable/utils';
import type { DisplayEnum, ViewabilityVendor } from '../../../../configs';
import { viewerLocalTimeLabel } from '../../../../configs';
import { TraffickingTableName } from '../../../../constants';
import bem from '../../../../utils/bem';
import { formatAdStartAndEndDateToCorrectFormat, formatFloatToPercentage } from '../../../../utils/formatting';
import { DEFAULT_COLUMN_MIN_WIDTH } from '../../constants';
import { AdColumn, AdColumnMeta } from '../../constants/ad';
import { getAlertMessage } from '../../ExportDropdown/utils/commonAccessors';
import {
  AdEditDateCell,
  AdFrequencyCapCell,
  AdStatusCell,
  AlertCell,
  CreateAndEditAdCell,
  DeleteAdCell,
  NameCell,
  ReviewCell,
  RotationGroupNameCell,
  SequenceWeightCell,
  TimeZoneCell,
  ViewabilityVendorCell,
} from '../cells';
import type { AlertFlagProps } from '../cells/AlertCell';
import { ALERT_MESSAGES, AlertFiled } from '../cells/AlertCell';
import InventoryBlockCell from '../cells/InventoryBlockCell';
import LineItemLinkCell from '../cells/LineItemLinkCell';
import SequenceCell from '../cells/SequenceCell';
import { EntityType } from '../hooks/makePageDrawerPlugin';
import type { AdModel } from '../modelConverters';
import { CellType } from '../types';
import type { CustomColumnConfig } from './utils';
import {
  getAdSequenceName,
  getPodPositionLabel,
  getPublisherTargetingList,
  getSlotPositionLabel,
  stringToDateTimeString,
} from './utils';

const [, element] = bem('alert-cell');

export const getAdsAlertFlags = (ad: AdModel): AlertFlagProps[] => {
  const { id, hasMisalignedSchedule, hasInvalidPublisher, hasInvalidCountry, lineItem } = ad;

  return [
    {
      id,
      field: AlertFiled.MisalignedSchedule,
      isShown: !!hasMisalignedSchedule,
      icon: <IconCalendar width={18} height={18} />,
      hoverMessage: ALERT_MESSAGES[AlertFiled.MisalignedSchedule],
      className: element('red-icon'),
    },
    {
      id,
      field: AlertFiled.InvalidPublisher,
      isShown: !!hasInvalidPublisher,
      icon: <IconAlertWarningSolid width={18} height={18} />,
      hoverMessage: ALERT_MESSAGES[AlertFiled.InvalidPublisher],
      className: element('red-icon'),
    },
    {
      id,
      field: AlertFiled.InvalidCountry,
      isShown: !!hasInvalidCountry,
      icon: <IconAlertWarningSolid width={18} height={18} />,
      hoverMessage: ALERT_MESSAGES[AlertFiled.InvalidCountry],
      className: element('red-icon'),
    },
    {
      id,
      field: AlertFiled.LineItem,
      isShown: !lineItem,
      icon: <Badge text={'LI'} />,
      hoverMessage: ALERT_MESSAGES[AlertFiled.LineItem],
      className: element('red-icon'),
    },
    {
      id,
      field: AlertFiled.InventoryBlock,
      icon: <InventoryBlockCell exclusiveBlock={lineItem?.exclusiveBlock} />,
      isShown: !!lineItem?.exclusiveBlock,
      hoverMessage: ALERT_MESSAGES[AlertFiled.InventoryBlock],
      className: element('red-icon', 'clickable'),
    },
  ];
};

// NOTE: `Cell` property cannot return `undefined` or the page will crash
const getAdsColumns = (): CustomColumnConfig<AdModel>[] => {
  let columns: CustomColumnConfig<AdModel>[] = [
    {
      id: AdColumn.Alert,
      Header: AdColumnMeta[AdColumn.Alert].header,
      Cell: (cell: Cell<AdModel>) => <AlertCell flags={getAdsAlertFlags(cell.row.original)} />,
      width: resizingData.getData(90, TraffickingTableName.campaigns, 'Alert'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Alert].tooltip,
      exportAccessor: (ad) =>
        getAlertMessage({
          hasMisalignedSchedule: !!ad.hasMisalignedSchedule,
          hasMissedLineItem: !ad.lineItem,
          hasLineItemExclusiveBlock: !!ad.lineItem?.inventoryBlockId,
          hasInvalidPublisher: !!ad.hasInvalidPublisher,
          hasInvalidCountry: !!ad.hasInvalidCountry,
        }),
    },
    {
      id: AdColumn.Name,
      accessor: 'name',
      Header: AdColumnMeta[AdColumn.Name].header,
      Cell: ({ row: { original }, openTraffickingPageDrawer }) => (
        <NameCell
          rowOriginal={original}
          entityType={EntityType.AD}
          openTraffickingPageDrawer={openTraffickingPageDrawer}
        />
      ),
      disableHiding: true,
      width: resizingData.getData(400, TraffickingTableName.ads, 'Name'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      className: stickyBlock('name'),
      tooltipMessage: AdColumnMeta[AdColumn.Name].tooltip,
    },
    {
      id: AdColumn.EditAd,
      Header: AdColumnMeta[AdColumn.EditAd].header,
      Cell: (cell: Cell<AdModel>) => <CreateAndEditAdCell cell={cell} cellType={CellType.edit} />,
      width: resizingData.getData(100, TraffickingTableName.ads, 'EditAd'),
      tooltipMessage: AdColumnMeta[AdColumn.EditAd].tooltip,
      disableForExport: true,
    },
    {
      id: AdColumn.TrackingId,
      accessor: 'trackingId',
      Header: AdColumnMeta[AdColumn.TrackingId].header,
      width: resizingData.getData(185, TraffickingTableName.ads, 'TrackingId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.TrackingId].tooltip,
      exportAccessor: (ad) => ad.trackingId ?? '',
    },
    {
      id: AdColumn.DeleteAd,
      Header: AdColumnMeta[AdColumn.DeleteAd].header,
      Cell: DeleteAdCell,
      isEditable: true,
      width: resizingData.getData(100, TraffickingTableName.ads, 'deleteAd'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      disableForExport: true,
    },
    {
      id: AdColumn.AdID,
      accessor: 'id',
      Header: AdColumnMeta[AdColumn.AdID].header,
      width: resizingData.getData(320, TraffickingTableName.ads, 'adID'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.AdID].tooltip,
    },
    {
      id: AdColumn.Countries,
      accessor: ({ countriesTarget }) => countriesTarget?.join(', ') ?? '',
      Header: AdColumnMeta[AdColumn.Countries].header,
      width: resizingData.getData(115, TraffickingTableName.lineItems, 'Country'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Countries].tooltip,
      exportAccessor: (ad) => ad.countriesTarget?.join(', ') ?? '',
    },
    {
      id: AdColumn.Type,
      accessor: (a) => a.type.displayName,
      Header: AdColumnMeta[AdColumn.Type].header,
      width: resizingData.getData(250, TraffickingTableName.ads, 'Type'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Type].tooltip,
      exportAccessor: (ad) => ad.type?.displayName ?? '',
    },
    {
      id: AdColumn.Status,
      accessor: (a) => a.status,
      Header: AdColumnMeta[AdColumn.Status].header,
      isEditable: true,
      Cell: AdStatusCell,
      width: resizingData.getData(130, TraffickingTableName.ads, 'Status'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Status].tooltip,
      exportAccessor: (ad) => ad.status?.displayName ?? '',
    },
    {
      id: AdColumn.Review,
      accessor: (a) => a.review,
      Header: AdColumnMeta[AdColumn.Review].header,
      Cell: ReviewCell,
      width: resizingData.getData(150, TraffickingTableName.ads, 'Review'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Review].tooltip,
      exportAccessor: (ad) => ad.review?.displayName ?? '',
    },
    {
      id: AdColumn.ScheduleStart,
      accessor: (ad) => ad.startDate,
      Header: (
        <DateHeaderLabel
          label={AdColumnMeta[AdColumn.ScheduleStart].header}
          subLabel={AdColumnMeta[AdColumn.ScheduleStart].subHeader}
          dataTestid="start-date-header"
        />
      ),
      headerString: AdColumnMeta[AdColumn.ScheduleStart].header,
      isEditable: true,
      Cell: (cellProps: CellProps<AdModel>): JSX.Element => <AdEditDateCell {...cellProps} />,
      width: resizingData.getData(210, TraffickingTableName.ads, 'ScheduleStart'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.ScheduleStart].tooltip,
      exportAccessor: (ad) => formatAdStartAndEndDateToCorrectFormat(ad.startDate) ?? '',
    },
    {
      id: AdColumn.ScheduleEnd,
      accessor: (ad) => ad.endDate,
      Header: (
        <DateHeaderLabel
          label={AdColumnMeta[AdColumn.ScheduleEnd].header}
          subLabel={AdColumnMeta[AdColumn.ScheduleEnd].subHeader}
          dataTestid="end-date-header"
        />
      ),
      headerString: AdColumnMeta[AdColumn.ScheduleEnd].header,
      isEditable: true,
      Cell: (cellProps: CellProps<AdModel>): JSX.Element => <AdEditDateCell {...cellProps} isAdStartDate={false} />,
      width: resizingData.getData(210, TraffickingTableName.ads, 'ScheduleEnd'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.ScheduleEnd].tooltip,
      exportAccessor: (ad) => formatAdStartAndEndDateToCorrectFormat(ad.endDate) ?? '',
    },
    {
      id: AdColumn.LegalApprovedCreative,
      accessor: (ad) => (ad.creative?.metadata?.isLegalApproved ? 'Yes' : ''),
      isEditable: false,
      Header: AdColumnMeta[AdColumn.LegalApprovedCreative].header,
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      width: resizingData.getData(225, TraffickingTableName.ads, 'legalApprovedCreative'),
      tooltipMessage: AdColumnMeta[AdColumn.LegalApprovedCreative].tooltip,
      exportAccessor: (ad) => (ad.creative?.metadata?.isLegalApproved ? 'Yes' : ''),
    },
    {
      id: AdColumn.DisplayPublisherTarget,
      accessor: 'displayPublisherTarget',
      Header: AdColumnMeta[AdColumn.DisplayPublisherTarget].header,
      Cell: getPublisherTargetingList,
      width: resizingData.getData(230, TraffickingTableName.lineItems, 'DisplayPublisherTarget'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.DisplayPublisherTarget].tooltip,
      exportAccessor: (ad) => getPublisherTargetingList({ value: ad.displayPublisherTarget }),
    },
    {
      id: AdColumn.ScheduleTimezoneOffset,
      accessor: (a) => ({ timezone: a.schedule?.timezone, deliveryInViewerTime: a.schedule?.deliveryInViewerTime }),
      Header: AdColumnMeta[AdColumn.ScheduleTimezoneOffset].header,
      Cell: TimeZoneCell,
      isEditable: true,
      width: resizingData.getData(310, TraffickingTableName.ads, 'ScheduleTimezoneOffset'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.ScheduleTimezoneOffset].tooltip,
      exportAccessor: (ad) => {
        if (!ad.schedule) return '';
        if (ad.schedule?.deliveryInViewerTime) return viewerLocalTimeLabel;
        return getTimezoneWithGMT(ad.schedule?.timezone);
      },
    },
    {
      id: AdColumn.Weight,
      Header: AdColumnMeta[AdColumn.Weight].header,
      isEditable: true,
      accessor: 'adRotation',
      Cell: ({ row: { original } }) => (
        <SequenceWeightCell weight={original.adRotation?.weight} lineItemId={original.lineItem?.id || ''} />
      ),
      width: resizingData.getData(140, TraffickingTableName.ads, 'Weight'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.Weight].tooltip,
      exportAccessor: (ad) => formatFloatToPercentage(ad.adRotation?.weight ?? 0),
    },
    {
      id: AdColumn.ViewabilityVendorList,
      accessor: 'viewabilityVendorList',
      Header: AdColumnMeta[AdColumn.ViewabilityVendorList].header,
      isEditable: true,
      Cell: ViewabilityVendorCell,
      width: resizingData.getData(220, TraffickingTableName.ads, 'ViewabilityVendorList'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.ViewabilityVendorList].tooltip,
      exportAccessor: (ad) =>
        (ad.viewabilityVendorList ?? []).map((vv: DisplayEnum<ViewabilityVendor>) => vv.displayName).join(' '),
    },
    {
      id: AdColumn.FrequencyCapList,
      accessor: (a) => a.frequencyCapList,
      isEditable: true,
      Header: AdColumnMeta[AdColumn.FrequencyCapList].header,
      Cell: AdFrequencyCapCell,
      width: resizingData.getData(385, TraffickingTableName.ads, 'frequencyCapList'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.FrequencyCapList].tooltip,
      exportAccessor: (ad) => (ad.frequencyCapList ? computeRenderValue(ad.frequencyCapList).replace(',', '') : ''),
    },
    {
      id: AdColumn.CreatedAt,
      accessor: 'createdAt',
      Header: AdColumnMeta[AdColumn.CreatedAt].header,
      Cell: stringToDateTimeString,
      width: resizingData.getData(190, TraffickingTableName.ads, 'CreatedAt'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.CreatedAt].tooltip,
      exportAccessor: (ad) => stringToDateTimeString({ value: ad.createdAt }) ?? '',
    },
    {
      id: AdColumn.UpdatedAt,
      accessor: 'updatedAt',
      Header: AdColumnMeta[AdColumn.UpdatedAt].header,
      Cell: stringToDateTimeString,
      width: resizingData.getData(185, TraffickingTableName.ads, 'UpdatedAt'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.UpdatedAt].tooltip,
      exportAccessor: (ad) => stringToDateTimeString({ value: ad.updatedAt }) ?? '',
    },
    {
      id: AdColumn.LineItemName,
      accessor: (a) => a.lineItem?.name,
      Header: AdColumnMeta[AdColumn.LineItemName].header,
      Cell: ({ row: { original } }: CellProps<AdModel>) =>
        original.lineItem?.id && original.lineItem?.name ? (
          <LineItemLinkCell lineItemId={original.lineItem.id} displayValue={original.lineItem.name} />
        ) : (
          <span>{original.lineItem?.name ?? ''}</span>
        ),
      width: resizingData.getData(675, TraffickingTableName.ads, 'LineItemName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.LineItemName].tooltip,
      exportAccessor: (ad) => ad.lineItem?.name ?? '',
    },
    {
      id: AdColumn.LineItemId,
      accessor: (a) => a.lineItem?.id,
      Header: AdColumnMeta[AdColumn.LineItemId].header,
      Cell: ({ row: { original } }: CellProps<AdModel>) =>
        original.lineItem?.id ? (
          <LineItemLinkCell lineItemId={original.lineItem.id} displayValue={original.lineItem.id} />
        ) : (
          <span>{''}</span>
        ),
      width: resizingData.getData(330, TraffickingTableName.ads, 'lineItemId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.LineItemId].tooltip,
      exportAccessor: (ad) => ad.lineItem?.id ?? '',
    },
    {
      id: AdColumn.CampaignName,
      accessor: (a) => a.lineItem?.campaign?.name,
      Header: AdColumnMeta[AdColumn.CampaignName].header,
      width: resizingData.getData(440, TraffickingTableName.ads, 'CampaignName'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.CampaignName].tooltip,
      exportAccessor: (ad) => ad.lineItem?.campaign?.name ?? '',
    },
    {
      id: AdColumn.CampaignId,
      accessor: (a) => a.lineItem?.campaign?.id,
      Header: AdColumnMeta[AdColumn.CampaignId].header,
      width: resizingData.getData(330, TraffickingTableName.ads, 'campaignId'),
      minWidth: DEFAULT_COLUMN_MIN_WIDTH,
      tooltipMessage: AdColumnMeta[AdColumn.CampaignId].tooltip,
      exportAccessor: (ad) => ad.lineItem?.campaign?.id ?? '',
    },
    {
      id: AdColumn.RotationName,
      accessor: (a) => a.adRotation,
      isEditable: true,
      Header: AdColumnMeta[AdColumn.RotationName].header,
      Cell: RotationGroupNameCell,
      minWidth: 330,
      width: resizingData.getData(330, TraffickingTableName.ads, 'RotationName'),
      tooltipMessage: AdColumnMeta[AdColumn.RotationName].tooltip,
      exportAccessor: (ad) => ad.adRotation?.rotationName ?? '',
    },
  ];

  if (envConfig.REACT_APP_FEATURE_ENABLE_CREATE_SEQUENCE === 'true') {
    columns = columns.concat([
      {
        id: AdColumn.SequenceId,
        accessor: (a) => a?.adSequence?.sequenceId,
        Cell: ({ row: { original } }: CellProps<AdModel>) => (
          <SequenceCell
            title={original?.adSequence?.sequenceId}
            parentLineOmsLinkId={original?.lineItem?.parentLineOmsLink?.id}
            lineItemId={original?.lineItem?.id}
          />
        ),
        isEditable: true,
        Header: AdColumnMeta[AdColumn.SequenceId].header,
        width: resizingData.getData(140, TraffickingTableName.ads, 'SequenceId'),
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        tooltipMessage: AdColumnMeta[AdColumn.SequenceId].tooltip,
        exportAccessor: (ad) => ad.adSequence?.sequenceId ?? '',
      },
      {
        id: AdColumn.SequenceName,
        accessor: (a) => a?.adSequence,
        Cell: ({ row: { original } }: CellProps<AdModel>) => (
          <SequenceCell
            title={getAdSequenceName(original?.adSequence)}
            parentLineOmsLinkId={original?.lineItem?.parentLineOmsLink?.id}
            lineItemId={original?.lineItem?.id}
          />
        ),
        isEditable: true,
        Header: AdColumnMeta[AdColumn.SequenceName].header,
        width: resizingData.getData(170, TraffickingTableName.ads, 'SequenceName'),
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        tooltipMessage: AdColumnMeta[AdColumn.SequenceName].tooltip,
        exportAccessor: (ad) => getAdSequenceName(ad?.adSequence),
      },
      {
        id: AdColumn.SequenceOrder,
        accessor: (a) => a?.adSequence?.order,
        Cell: ({ row: { original } }: CellProps<AdModel>) => (
          <SequenceCell
            title={original?.adSequence?.order}
            parentLineOmsLinkId={original?.lineItem?.parentLineOmsLink?.id}
            lineItemId={original?.lineItem?.id}
          />
        ),
        isEditable: true,
        Header: AdColumnMeta[AdColumn.SequenceOrder].header,
        width: resizingData.getData(140, TraffickingTableName.ads, 'SequenceOrder'),
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        tooltipMessage: AdColumnMeta[AdColumn.SequenceOrder].tooltip,
        exportAccessor: (ad) => ad.adSequence?.order?.toString() ?? '',
      },
      {
        id: AdColumn.PodPosition,
        accessor: (a) => getPodPositionLabel(a?.adSequence?.podPosition),
        Header: AdColumnMeta[AdColumn.PodPosition].header,
        width: resizingData.getData(160, TraffickingTableName.ads, 'PodPosition'),
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        tooltipMessage: AdColumnMeta[AdColumn.PodPosition].tooltip,
        exportAccessor: (ad) => getPodPositionLabel(ad?.adSequence?.podPosition),
      },
      {
        id: AdColumn.SlotPosition,
        accessor: (a) => getSlotPositionLabel(a?.adSequence?.podAlignment),
        Header: AdColumnMeta[AdColumn.SlotPosition].header,
        width: resizingData.getData(160, TraffickingTableName.ads, 'SlotPosition'),
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        tooltipMessage: AdColumnMeta[AdColumn.SlotPosition].tooltip,
        exportAccessor: (ad) => getSlotPositionLabel(ad?.adSequence?.podAlignment),
      },
    ]);
  }

  return columns;
};

export default getAdsColumns;
